import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import Mixpanel from '../utils/mixpanel';
import axios from 'axios';
import API_URL from '../config';
import { useUser } from '../context/UserContext';
import { CheckCircle2, Circle, CheckSquare, Square, Play, Pause, Volume2, Loader } from 'lucide-react';
import logger from '../utils/logger';
import InvitationHandler from './InvitationHandler';
import DailyCheckIn from './DailyCheckIn';
import GuideModal from './GuideModal';

const Dashboard = () => {
  // State for weekly performance metrics
  const [weeklyMetrics, setWeeklyMetrics] = useState({
    loginCount: 0,
    journalCount: 0,
    breathworkCount: 0,
    breathCycles: 0,
    relaxationMinutes: 0,
    habitCompletionPercentage: 0,
    concentrationGames: 0,
    loading: true
  });
  
  // State for team invitations
  const [showInvitation, setShowInvitation] = useState(false);
  
  // State for incomplete daily checklist items
  const [incompleteChecklist, setIncompleteChecklist] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(true);
  
  // State for habits (Success Checklist items)
  const [habits, setHabits] = useState([]);
  const [habitsLoading, setHabitsLoading] = useState(true);
  
  // State for logging cached content
  const [hasLoggedCachedContent, setHasLoggedCachedContent] = useState(false);
  
  // State to track if this is a new user session
  const [isNewUserSession, setIsNewUserSession] = useState(false);
  
  // State for starter tasks
  const [starterTasks, setStarterTasks] = useState({
    onboardingCompleted: false,
    motivationCompleted: false,
    mindsetCompleted: false,
    firstHabitCheck: false,
    firstBreathCycle: false,
    firstRelaxationSession: false,
    firstConcentrationGame: false,
    firstJournalEntry: false,
    hasOnboardingAudio: false,
    hasMentalImagery: false,
    loading: true
  });
  
  // State to control whether to show starter tasks
  const [showStarterTasks, setShowStarterTasks] = useState(false);
  
  // Audio state management
  const [audioLoading, setAudioLoading] = useState(false);
  const [mindsetAudioData, setMindsetAudioData] = useState(null);
  const [mentalImageryAudioData, setMentalImageryAudioData] = useState(null);
  const [mindsetAudioPlaying, setMindsetAudioPlaying] = useState(false);
  const [mentalImageryAudioPlaying, setMentalImageryAudioPlaying] = useState(false);
  const mindsetAudioRef = useRef(null);
  const mentalImageryAudioRef = useRef(null);
  
  // Sequential audio generation status state
  const [mindsetAudioGenerating, setMindsetAudioGenerating] = useState(false);
  const [mindsetAudioReady, setMindsetAudioReady] = useState(false);
  const [mindsetAudioError, setMindsetAudioError] = useState(false);
  const [mentalImageryAudioGenerating, setMentalImageryAudioGenerating] = useState(false);
  const [mentalImageryAudioWaiting, setMentalImageryAudioWaiting] = useState(false);
  const [mentalImageryAudioReady, setMentalImageryAudioReady] = useState(false);
  const [mentalImageryAudioError, setMentalImageryAudioError] = useState(false);
  
  // Get user and navigation
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const metrics = {
    streaks: {
      currentStreak: 5,
      longestStreak: 14
    },
    completionRate: 85,
    focusScore: 78
  };

  // Track dashboard view when component mounts
  useEffect(() => {
    // Track with detailed page information
    Mixpanel.trackEnhancedPageView('/dashboard', 'Dashboard', {
      feature: 'Dashboard',
      action: 'Opened'
    });
    
    // Check for invitation in URL
    const queryParams = new URLSearchParams(location.search);
    const invitationCode = queryParams.get('invitation');
    if (invitationCode) {
      setShowInvitation(true);
    }
    
    // Check if this is a new user session
    const lastLoginTime = localStorage.getItem('lastLoginTime');
    const currentTime = new Date().getTime();
    if (!lastLoginTime) {
      // First login ever
      setIsNewUserSession(true);
      localStorage.setItem('lastLoginTime', currentTime);
    }
    
    // Fetch data with a slight delay for new users to prevent overwhelming API calls
    if (isNewUserSession) {
      // For new users, delay the API calls slightly to prioritize onboarding
      setTimeout(() => {
        fetchWeeklyMetrics().then(() => {
          fetchStarterTasksStatus();
        });
        fetchIncompleteChecklist();
        fetchHabits();
      }, 1000);
    } else {
      // For returning users, fetch data immediately
      fetchWeeklyMetrics().then(() => {
        fetchStarterTasksStatus();
      });
      fetchIncompleteChecklist();
      fetchHabits();
    }
  }, []);

  // Helper function to format date
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch starter tasks status
  const fetchStarterTasksStatus = async () => {
    try {
      setStarterTasks(prev => ({ ...prev, loading: true }));
      
      // Fallback: Check local storage for cached values
      const onboardingCompleted = localStorage.getItem('onboardingCompleted') === 'true';
      const motivationCompleted = localStorage.getItem('motivationCompleted') === 'true';
      const mindsetCompleted = localStorage.getItem('mindsetCompleted') === 'true';
      const firstHabitCheck = localStorage.getItem('firstHabitCheck') === 'true';
      const firstBreathCycle = localStorage.getItem('firstBreathCycle') === 'true';
      const firstRelaxationSession = localStorage.getItem('firstRelaxationSession') === 'true';
      const firstConcentrationGame = localStorage.getItem('firstConcentrationGame') === 'true';
      const firstJournalEntry = localStorage.getItem('firstJournalEntry') === 'true';
      const hasOnboardingAudio = localStorage.getItem('onboardingMindsetAudioId') !== null;
      const hasMentalImagery = localStorage.getItem('onboardingMentalImageryId') !== null;
      
      // Try to get user data from API to update task status
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.get(`${API_URL}/api/users/info`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const apiData = response.data;
          logger.debug('User data for starter tasks:', apiData);
          
          // If tasks are completed according to metrics but not marked in localStorage, update localStorage
          if (weeklyMetrics.habitCompletionPercentage > 0 && !firstHabitCheck) {
            localStorage.setItem('firstHabitCheck', 'true');
          }
          
          if (weeklyMetrics.breathCycles > 0 && !firstBreathCycle) {
            localStorage.setItem('firstBreathCycle', 'true');
          }
          
          if (weeklyMetrics.relaxationMinutes > 0 && !firstRelaxationSession) {
            localStorage.setItem('firstRelaxationSession', 'true');
          }
          
          if (weeklyMetrics.concentrationGames > 0 && !firstConcentrationGame) {
            localStorage.setItem('firstConcentrationGame', 'true');
          }
          
          if (weeklyMetrics.journalCount > 0 && !firstJournalEntry) {
            localStorage.setItem('firstJournalEntry', 'true');
          }
        }
      } catch (userInfoError) {
        logger.error('Error fetching user info for starter tasks:', userInfoError);
      }
      
      // Update state with local storage values
      const updatedTaskStatus = {
        onboardingCompleted,
        motivationCompleted,
        mindsetCompleted,
        firstHabitCheck,
        firstBreathCycle,
        firstRelaxationSession,
        firstConcentrationGame,
        firstJournalEntry,
        hasOnboardingAudio,
        hasMentalImagery,
        loading: false
      };
      
      setStarterTasks(updatedTaskStatus);
      
      // Count completed tasks
      const completedTasksCount = Object.entries(updatedTaskStatus)
        .filter(([key, value]) => value === true && key !== 'loading')
        .length;
      
      // Show starter tasks if less than 5 tasks are completed (excluding loading property)
      setShowStarterTasks(completedTasksCount < 5);
      
    } catch (error) {
      logger.error('Error fetching starter tasks status:', error);
      setStarterTasks(prev => ({ ...prev, loading: false }));
    }
  };

  // Helper function to update starter task status on server
  const updateStarterTaskOnServer = async (task, completed) => {
    // Store in localStorage
    localStorage.setItem(task, completed ? 'true' : 'false');
    
    // Note: We're skipping the API call for now since the endpoint doesn't exist yet
    // This will be implemented when the backend is updated
  };

  // Fetch weekly performance metrics
  const fetchWeeklyMetrics = async () => {
    try {
      logger.debug('Fetching weekly metrics...');
      const token = localStorage.getItem('token');
      
      // Get local concentration grid data regardless of API success
      const localGridAttempts = parseInt(localStorage.getItem('concentrationGridAttempts') || '0');
      const localGridLastPlayed = localStorage.getItem('concentrationGridLastPlayed');
      
      logger.debug('Local concentration grid data:', { attempts: localGridAttempts, lastPlayed: localGridLastPlayed });
      
      if (!token) {
        logger.debug('No token found, using local data only');
        // Use local data when no token is available
        setWeeklyMetrics(prev => ({
          ...prev,
          concentrationGames: localGridAttempts,
          loading: false
        }));
        return;
      }

      // Use consistent API URL construction
      const response = await axios.get(`${API_URL}/api/users/weekly-performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      logger.debug('Weekly metrics response:', response.data);
      
      // Merge API data with local data
      const apiData = response.data;
      const mergedData = {
        // Ensure we have all the required metrics with proper fallbacks
        loginCount: apiData.loginCount || 0,
        journalCount: apiData.journalCount || 0,
        breathworkCount: apiData.breathworkCount || 0,
        breathCycles: apiData.breathCycles || 0,
        relaxationMinutes: apiData.relaxationMinutes || apiData.meditationMinutes || 0,
        habitCompletionPercentage: apiData.habitCompletionPercentage || 0,
        // Use the higher value between API and local data for concentration games
        concentrationGames: Math.max(apiData.concentrationGames || 0, localGridAttempts)
      };
      
      logger.debug('Merged metrics data:', mergedData);
      
      setWeeklyMetrics({
        ...mergedData,
        loading: false
      });
      
      // After metrics are loaded, update starter tasks status
      fetchStarterTasksStatus();
      
      // Log cached content once
      if (!hasLoggedCachedContent) {
        logCachedContent();
      }
      
    } catch (error) {
      logger.error('Error fetching weekly metrics:', error);
      
      // Use local data on error
      const localGridAttempts = parseInt(localStorage.getItem('concentrationGridAttempts') || '0');
      const localGridLastPlayed = localStorage.getItem('concentrationGridLastPlayed');
      
      setWeeklyMetrics(prev => ({
        ...prev,
        concentrationGames: localGridAttempts,
        loading: false
      }));
    }
  };
  
  // Fetch incomplete daily checklist items
  const fetchIncompleteChecklist = async () => {
    try {
      setChecklistLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        logger.debug('No token found for checklist');
        setChecklistLoading(false);
        return;
      }
      
      // Use the correct endpoint without date parameter
      const response = await axios.get(`${API_URL}/api/daily-actions/incomplete-today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      logger.debug('Incomplete checklist response:', response.data);
      setIncompleteChecklist(response.data);
      
    } catch (error) {
      logger.error('Error fetching incomplete checklist:', error);
    } finally {
      setChecklistLoading(false);
    }
  };
  
  // Fetch habits (Success Checklist items)
  const fetchHabits = async () => {
    try {
      setHabitsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        logger.error('No token found');
        setHabitsLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/daily-actions`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setHabits(response.data);
      } else {
        logger.error('Invalid response format for habits');
        setHabits([]);
      }
    } catch (error) {
      logger.error('Error fetching habits:', error);
      setHabits([]);
    } finally {
      setHabitsLoading(false);
    }
  };

  // Toggle habit status for today
  const toggleHabitStatus = async (actionId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        logger.error('No token found');
        return;
      }
      
      // Get today's day code (MO, TU, WE, etc.) - reordered to start on Monday
      const today = new Date();
      const daysOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
      // Adjust getDay() to make Monday first day (0) and Sunday last day (6)
      const todayCode = daysOfWeek[(today.getDay() + 6) % 7];
      
      // Find the habit to toggle
      const habit = habits.find(h => h._id === actionId);
      if (!habit) return;
      
      // Find today's status
      const todayStatus = habit.dailyStatus?.find(s => s.day === todayCode);
      const currentStatus = todayStatus?.status || 'pending';
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      
      // Update UI optimistically
      setHabits(prevHabits => 
        prevHabits.map(h => {
          if (h._id === actionId) {
            return {
              ...h,
              dailyStatus: h.dailyStatus.map(s => 
                s.day === todayCode ? { ...s, status: newStatus } : s
              )
            };
          }
          return h;
        })
      );
      
      // Send API request to update status
      await axios.put(`${API_URL}/api/daily-actions/${actionId}/day/${todayCode}`, {
        status: newStatus
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Track first habit check completion
      if (newStatus === 'completed' && !starterTasks.firstHabitCheck) {
        setStarterTasks(prev => ({ ...prev, firstHabitCheck: true }));
        localStorage.setItem('firstHabitCheck', 'true');
      }
      
      // Refresh metrics after updating habit status
      fetchWeeklyMetrics();
      
    } catch (error) {
      logger.error('Error toggling habit status:', error);
      // Revert UI on error
      fetchHabits();
    }
  };

  // Get today's status for a habit
  const getHabitStatusForToday = (habit) => {
    if (!habit || !habit.dailyStatus) return 'pending';
    
    // Reordered days to start on Monday and end on Sunday
    const daysOfWeek = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    // Adjust getDay() to make Monday first day (0) and Sunday last day (6)
    const todayCode = daysOfWeek[(new Date().getDay() + 6) % 7];
    
    const todayStatus = habit.dailyStatus.find(s => s.day === todayCode);
    return todayStatus?.status || 'pending';
  };

  // Handle checklist item completion
  const handleChecklistItemClick = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const today = new Date().toISOString().split('T')[0];
      
      // Update the API endpoint to match the backend route
      await axios.put(`${API_URL}/api/daily-actions/${itemId}/day/${today}`, 
        { completed: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update local state to remove the completed item
      setIncompleteChecklist(prev => prev.filter(item => item._id !== itemId));
      
      // Track the action
      Mixpanel.trackFeatureAction('Dashboard', 'Checklist Item Completed', {
        timestamp: new Date().toISOString()
      });
      
      // Mark first habit check as completed if not already
      if (!starterTasks.firstHabitCheck) {
        setStarterTasks(prev => ({ ...prev, firstHabitCheck: true }));
        localStorage.setItem('firstHabitCheck', 'true');
        
        // Update on server
        updateStarterTaskOnServer('firstHabitCheck', true);
      }
      
    } catch (error) {
      logger.error('Error completing checklist item:', error);
    }
  };
  
  // Handle starter task navigation
  const handleStarterTaskClick = (taskType) => {
    switch(taskType) {
      case 'onboarding':
        // setShowOnboarding(true);
        break;
      case 'motivation':
        navigate('/motivation');
        // Mark motivation task as completed
        setStarterTasks(prev => ({ ...prev, motivationCompleted: true }));
        localStorage.setItem('motivationCompleted', 'true');
        updateStarterTaskOnServer('motivationCompleted', true);
        break;
      case 'mindset':
        navigate('/mindset');
        // Mark mindset task as completed
        setStarterTasks(prev => ({ ...prev, mindsetCompleted: true }));
        localStorage.setItem('mindsetCompleted', 'true');
        updateStarterTaskOnServer('mindsetCompleted', true);
        break;
      case 'habit':
        // Scroll to habits section
        document.querySelector('.dashboard-card:nth-child(2)').scrollIntoView({ behavior: 'smooth' });
        break;
      case 'breath':
        navigate('/dashboard?modal=breathwork');
        break;
      case 'relaxation':
        navigate('/dashboard?modal=relaxation');
        break;
      case 'concentration':
        navigate('/dashboard?modal=concentration');
        break;
      case 'journal':
        navigate('/journal');
        break;
      default:
        break;
    }
    
    // Track starter task click
    Mixpanel.trackFeatureAction('Dashboard', 'Starter Task Clicked', {
      task: taskType,
      timestamp: new Date().toISOString()
    });
  };
  
  // Track tool selection
  const handleToolClick = (toolName) => {
    Mixpanel.trackFeatureAction('Dashboard', 'Tool Selected', {
      tool: toolName,
      timestamp: new Date().toISOString()
    });
  };
  
  // Handle onboarding completion
  const handleOnboardingClose = async (data) => {
    logger.debug('Onboarding completed with data:', data);
    // setShowOnboarding(false);
    
    // Mark onboarding as completed in localStorage
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.setItem('onboardingNeeded', 'false');
    
    // Update starter tasks
    setStarterTasks(prev => ({ ...prev, onboardingCompleted: true }));
    
    // Track onboarding completion
    Mixpanel.trackFeatureAction('Onboarding', 'Completed', {
      timestamp: new Date().toISOString()
    });
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Format and save specific onboarding responses for quick access
      if (data) {
        // Save sport information
        if (data.sport) {
          localStorage.setItem('userSport', data.sport);
        }
        
        // Save why I play response
        if (data.whyIPlaySport) {
          localStorage.setItem('whyIPlay', data.whyIPlaySport);
        }
        
        // Save journey, goals, expectations as direct string values
        if (data.journey) {
          localStorage.setItem('journey', data.journey);
        }
        
        if (data.goals) {
          localStorage.setItem('goals', data.goals);
        }
        
        if (data.expectations) {
          localStorage.setItem('expectations', data.expectations);
        }
      }
      
      // Refresh user data to get the latest profile after onboarding
      logger.debug('Refreshing user data after onboarding completion');
      
      try {
        // Try to get user profile data using the profile endpoint
        const userResponse = await axios.get(`${API_URL}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (userResponse.data) {
          logger.debug('User profile refreshed after onboarding:', userResponse.data);
          // Update local storage with user data
          localStorage.setItem('user', JSON.stringify(userResponse.data));
        }
      } catch (profileError) {
        logger.error('Error fetching user profile:', profileError);
      }
    } catch (error) {
      logger.error('Error in onboarding completion process:', error);
    }
  };
  
  // Check if onboarding is needed
  const checkOnboardingStatus = async () => {
    try {
      logger.debug('Checking onboarding status...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        logger.debug('No token found for onboarding check');
        return;
      }
      
      // First, check if onboarding is explicitly marked as not needed
      const onboardingNeeded = localStorage.getItem('onboardingNeeded');
      if (onboardingNeeded === 'false') {
        logger.debug('Onboarding explicitly marked as not needed');
        return;
      }
      
      // Next, check local storage for onboarding completion
      const onboardingCompleted = localStorage.getItem('onboardingCompleted');
      if (onboardingCompleted === 'true') {
        logger.debug('Onboarding already completed according to localStorage');
        setStarterTasks(prev => ({ ...prev, onboardingCompleted: true }));
        return;
      }
      
      // If not in localStorage, check with the server - use /users/info instead of /users/me
      const response = await axios.get(`${API_URL}/api/users/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = response.data;
      logger.debug('User data for onboarding check:', userData);
      
      // Check multiple indicators that onboarding might be completed
      const hasOnboardingResponses = 
        userData.onboardingResponses && 
        Object.keys(userData.onboardingResponses).length > 0;
      
      const isOnboardingCompletedInProfile = userData.onboardingCompleted === true;
      
      const hasMvpData = userData.mvp && Object.keys(userData.mvp).length > 0;
      
      const hasMindsetData = userData.mindset && Object.keys(userData.mindset).length > 0;
      
      logger.debug('Onboarding indicators:', {
        hasOnboardingResponses,
        isOnboardingCompletedInProfile,
        hasMvpData,
        hasMindsetData
      });
      
      // If any of these conditions are true, consider onboarding complete
      if (hasOnboardingResponses || isOnboardingCompletedInProfile || hasMvpData || hasMindsetData) {
        // User has already completed onboarding
        logger.debug('User has completed onboarding based on profile data');
        localStorage.setItem('onboardingCompleted', 'true');
        localStorage.setItem('onboardingNeeded', 'false');
        setStarterTasks(prev => ({ ...prev, onboardingCompleted: true }));
        return;
      }
      
      // If we get here, the user hasn't completed onboarding
      logger.debug('User has not completed onboarding');
      // setShowOnboarding(true);
      
    } catch (error) {
      logger.error('Error checking onboarding status:', error);
      
      // If there's an error, we'll check localStorage one more time
      const onboardingCompleted = localStorage.getItem('onboardingCompleted');
      if (onboardingCompleted === 'true') {
        logger.debug('Using cached onboarding status after API error');
        setStarterTasks(prev => ({ ...prev, onboardingCompleted: true }));
        return;
      }
      
      const onboardingNeeded = localStorage.getItem('onboardingNeeded');
      if (onboardingNeeded === 'false') {
        logger.debug('Onboarding marked as not needed in localStorage');
        return;
      }
      
      // If we still can't determine, we'll assume onboarding is needed
      // but we'll delay it slightly to not interfere with initial load
      logger.debug('Could not determine onboarding status');
      // setTimeout(() => {
      //   setShowOnboarding(true);
      // }, 1500);
    }
  };
  
  // Log cached content for debugging
  const logCachedContent = () => {
    try {
      logger.debug('Logging cached content...');
      setHasLoggedCachedContent(true);
      
      // Log all localStorage items related to the app
      const relevantKeys = [
        'token',
        'user',
        'onboardingCompleted',
        'motivationCompleted',
        'mindsetCompleted',
        'firstHabitCheck',
        'firstBreathCycle',
        'firstRelaxationSession',
        'firstConcentrationGame',
        'userSport',
        'whyIPlay',
        'journey',
        'goals',
        'expectations',
        'concentrationGridAttempts',
        'concentrationGridLastPlayed',
        'lastLoginTime'
      ];
      
      const cachedData = {};
      relevantKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            // Try to parse as JSON first
            cachedData[key] = JSON.parse(value);
          } catch {
            // If not valid JSON, store as string
            cachedData[key] = value;
          }
        }
      });
      
      logger.debug('Cached content:', cachedData);
      
    } catch (error) {
      logger.error('Error logging cached content:', error);
    }
  };

  // Fetch audio files for Mindset Audio and Mental Imagery
  const fetchAudioFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      setAudioLoading(true);
      
      // Fetch all user audio files
      const audioResponse = await axios.get(`${API_URL}/api/ai/audio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (audioResponse.data && audioResponse.data.length > 0) {
        // Find Mindset Audio (type: 'mindset')
        const mindsetAudio = audioResponse.data.find(audio => 
          audio.type === 'mindset' && audio.audioUrl && audio.audioUrl !== 'pending'
        );
        
        // Find Mental Imagery Audio (type: 'mental-imagery')
        const mentalImageryAudio = audioResponse.data.find(audio => 
          audio.type === 'mental-imagery' && audio.audioUrl && audio.audioUrl !== 'pending'
        );
        
        setMindsetAudioData(mindsetAudio);
        setMentalImageryAudioData(mentalImageryAudio);
        
        logger.debug('Dashboard audio data fetched:', { mindsetAudio, mentalImageryAudio });
      }
    } catch (error) {
      logger.error('Error fetching audio files for Dashboard:', error);
    } finally {
      setAudioLoading(false);
    }
  };

  // Handle Mindset Audio playback
  const handleMindsetAudioPlay = () => {
    if (mindsetAudioRef.current && mindsetAudioData) {
      // Pause mental imagery audio if playing
      if (mentalImageryAudioPlaying && mentalImageryAudioRef.current) {
        mentalImageryAudioRef.current.pause();
        setMentalImageryAudioPlaying(false);
      }
      
      mindsetAudioRef.current.play();
      setMindsetAudioPlaying(true);
    }
  };

  const handleMindsetAudioPause = () => {
    if (mindsetAudioRef.current) {
      mindsetAudioRef.current.pause();
      setMindsetAudioPlaying(false);
    }
  };

  // Handle Mental Imagery Audio playback
  const handleMentalImageryAudioPlay = () => {
    if (mentalImageryAudioRef.current && mentalImageryAudioData) {
      // Pause mindset audio if playing
      if (mindsetAudioPlaying && mindsetAudioRef.current) {
        mindsetAudioRef.current.pause();
        setMindsetAudioPlaying(false);
      }
      
      mentalImageryAudioRef.current.play();
      setMentalImageryAudioPlaying(true);
    }
  };

  const handleMentalImageryAudioPause = () => {
    if (mentalImageryAudioRef.current) {
      mentalImageryAudioRef.current.pause();
      setMentalImageryAudioPlaying(false);
    }
  };

  // Audio event handlers
  const handleMindsetAudioEnded = () => {
    setMindsetAudioPlaying(false);
  };

  const handleMentalImageryAudioEnded = () => {
    setMentalImageryAudioPlaying(false);
  };

  useEffect(() => {
    fetchAudioFiles();
  }, []);

  // Auto-refresh functionality for audio generation completion
  useEffect(() => {
    let audioPollingInterval;
    
    const checkAudioGenerationStatus = () => {
      // Check localStorage flags for audio generation status
      const mindsetAudioGenerating = localStorage.getItem('mindsetAudioGenerating') === 'true';
      const mindsetAudioReady = localStorage.getItem('mindsetAudioReady') === 'true';
      const mentalImageryAudioGenerating = localStorage.getItem('mentalImageryAudioGenerating') === 'true';
      const mentalImageryAudioReady = localStorage.getItem('mentalImageryAudioReady') === 'true';
      
      logger.debug('Audio generation status check:', {
        mindsetAudioGenerating,
        mindsetAudioReady,
        mentalImageryAudioGenerating,
        mentalImageryAudioReady
      });
      
      // Update state based on localStorage flags
      setMindsetAudioGenerating(mindsetAudioGenerating);
      setMindsetAudioReady(mindsetAudioReady);
      setMentalImageryAudioGenerating(mentalImageryAudioGenerating);
      setMentalImageryAudioReady(mentalImageryAudioReady);
      
      // Auto-refresh when audio becomes ready
      if (mindsetAudioReady && !mindsetAudioData) {
        logger.info('Mindset Audio is ready - fetching updated audio data');
        // Clear the flag to prevent repeated refreshes
        localStorage.removeItem('mindsetAudioReady');
        localStorage.removeItem('mindsetAudioGenerating');
        // Fetch updated audio data instead of page reload
        fetchAudioFiles();
        return;
      }
      
      if (mentalImageryAudioReady && !mentalImageryAudioData) {
        logger.info('Mental Imagery Audio is ready - fetching updated audio data');
        // Clear the flag to prevent repeated refreshes
        localStorage.removeItem('mentalImageryAudioReady');
        localStorage.removeItem('mentalImageryAudioGenerating');
        // Fetch updated audio data instead of page reload
        fetchAudioFiles();
        return;
      }
    };
    
    // Check immediately on component mount
    checkAudioGenerationStatus();
    
    // Set up polling interval to check every 3 seconds if any audio is generating
    const mindsetGenerating = localStorage.getItem('mindsetAudioGenerating') === 'true';
    const mentalImageryGenerating = localStorage.getItem('mentalImageryAudioGenerating') === 'true';
    
    if (mindsetGenerating || mentalImageryGenerating) {
      logger.debug('Starting audio generation status polling');
      audioPollingInterval = setInterval(checkAudioGenerationStatus, 3000);
    }
    
    // Cleanup interval on component unmount
    return () => {
      if (audioPollingInterval) {
        logger.debug('Clearing audio generation status polling interval');
        clearInterval(audioPollingInterval);
      }
    };
  }, [mindsetAudioData, mentalImageryAudioData]); // Re-run when audio data changes

  return (
    <div className="min-h-screen bg-base-200 pb-8">
      {/* Show invitation handler if there's an invitation code in the URL */}
      {showInvitation && (
        <div className="pt-4">
          <InvitationHandler />
        </div>
      )}
      
      <div className="pt-4">
        <GuideModal guideKey="Dashboard" />
        <h1 className="text-2xl md:text-3xl font-bold mb-4 text-primary">Dashboard</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
          {/* Starter Tasks - temporarily hidden */}
          {/*
          {showStarterTasks && (
            <div className="card bg-base-100 shadow-md mb-4 border-2 border-primary">
              <div className="card-body p-4 md:p-6">
                <h2 className="card-title text-lg md:text-xl font-semibold mb-2 md:mb-4 text-primary">
                  Get Started 
                </h2>
                
                {starterTasks.loading ? (
                  <div className="flex justify-center py-4">
                    <span className="loading loading-spinner loading-md text-primary"></span>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.onboardingCompleted ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.onboardingCompleted && handleStarterTaskClick('onboarding')}
                    >
                      {starterTasks.onboardingCompleted ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">Finish Onboarding</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.motivationCompleted ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.motivationCompleted && handleStarterTaskClick('motivation')}
                    >
                      {starterTasks.motivationCompleted ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">Finish Motivation</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.mindsetCompleted ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.mindsetCompleted && handleStarterTaskClick('mindset')}
                    >
                      {starterTasks.mindsetCompleted ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">Finish Mindset</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.firstJournalEntry ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.firstJournalEntry && handleStarterTaskClick('journal')}
                    >
                      {starterTasks.firstJournalEntry ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">1st Journal Entry</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.firstHabitCheck ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.firstHabitCheck && handleStarterTaskClick('habit')}
                    >
                      {starterTasks.firstHabitCheck ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">First Habit Check</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.firstBreathCycle ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.firstBreathCycle && handleStarterTaskClick('breath')}
                    >
                      {starterTasks.firstBreathCycle ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">1st Breath Cycle</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.firstRelaxationSession ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.firstRelaxationSession && handleStarterTaskClick('relaxation')}
                    >
                      {starterTasks.firstRelaxationSession ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">1st Relaxation Session</span>
                    </li>
                    
                    <li 
                      className={`flex items-center p-2 rounded-lg cursor-pointer ${starterTasks.firstConcentrationGame ? 'bg-success/10' : 'bg-base-200 hover:bg-base-300'}`}
                      onClick={() => !starterTasks.firstConcentrationGame && handleStarterTaskClick('concentration')}
                    >
                      {starterTasks.firstConcentrationGame ? 
                        <CheckCircle2 className="h-5 w-5 text-success mr-3 flex-shrink-0" /> : 
                        <Circle className="h-5 w-5 text-base-content/50 mr-3 flex-shrink-0" />
                      }
                      <span className="text-sm md:text-base">1st Concentration Game</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          )}
          
          {/* Weekly Performance */}
          <div className="card bg-base-100 shadow-md">
            <div className="card-body p-4 md:p-6">
              <h2 className="card-title text-lg md:text-xl font-semibold mb-2 md:mb-4 text-primary">Weekly Performance</h2>
              
              {weeklyMetrics.loading ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-base-content/70">Total Logins</span>
                    <div className="badge badge-primary badge-lg">{weeklyMetrics.loginCount || 0}</div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-base-content/70">Journal Entries</span>
                    <div className="badge badge-primary badge-lg">{weeklyMetrics.journalCount || 0}</div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-base-content/70">Habit Track</span>
                    <div className="radial-progress text-primary" style={{"--value": weeklyMetrics.habitCompletionPercentage || 0, "--size": "2.5rem"}}>
                      <span className="text-xs">{weeklyMetrics.habitCompletionPercentage || 0}%</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-base-content/70">Breath Cycles</span>
                    <div className="badge badge-primary badge-lg">{weeklyMetrics.breathCycles || 0}</div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-base-content/70">Relaxation Minutes</span>
                    <div className="badge badge-primary badge-lg">{weeklyMetrics.relaxationMinutes || 0} min</div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-base-content/70">Concentration Games</span>
                    <div className="badge badge-primary badge-lg">{weeklyMetrics.concentrationGames || 0}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* My Habits */}
          <div className="card bg-base-100 shadow-md">
            <div className="card-body p-4 md:p-6">
              <h2 className="card-title text-lg md:text-xl font-semibold mb-2 md:mb-4 text-primary">My Habits</h2>
              
              {habitsLoading ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                </div>
              ) : habits.length > 0 ? (
                <ul className="space-y-3">
                  {habits.map(habit => (
                    <li 
                      key={habit._id} 
                      className="flex items-center p-2 bg-base-200 hover:bg-base-300 rounded-lg"
                    >
                      <div className="inline-flex items-center">
                        <button 
                          onClick={() => toggleHabitStatus(habit._id)}
                          className="flex-none p-0"
                          title={getHabitStatusForToday(habit) === 'completed' ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          {getHabitStatusForToday(habit) === 'completed' ? (
                            <CheckSquare className="h-6 w-6 text-success" />
                          ) : (
                            <Square className="h-6 w-6" />
                          )}
                        </button>
                        <span className="ml-1 text-sm md:text-base">{habit.action}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="alert">
                  <div>
                    <span className="text-sm md:text-base">You have not created any habits yet.</span>
                    <a href="https://www.mentaledgecoach.net/daily-success-checklist" className="btn btn-primary btn-sm ml-2" target="_blank" rel="noopener noreferrer">Start Now</a>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Onboarding Audio */}
          {(starterTasks.hasOnboardingAudio || mindsetAudioData) && (
            <div className="bg-white shadow-md rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-4 text-primary">Your Personalized Mindset Audio</h2>
              <p className="text-gray-600 mb-4">
                We've created a personalized mindset audio for you based on your profile. 
                Listen to it before practice or competition to get in the right mindset.
              </p>
              {/* Link to Mindset Audio - with audio playback */}
              <div className="flex justify-center">
                {audioLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="flex flex-col items-center">
                      <span className="loading loading-spinner loading-md text-primary mb-2"></span>
                      <span className="text-sm text-gray-600">Audio file is being generated...</span>
                    </div>
                  </div>
                ) : mindsetAudioData ? (
                  <div>
                    <button
                      onClick={mindsetAudioPlaying ? handleMindsetAudioPause : handleMindsetAudioPlay}
                      className="btn btn-accent"
                    >
                      {mindsetAudioPlaying ? (
                        <>
                          <Pause className="h-5 w-5 mr-2" />
                          Pause Mindset Audio
                        </>
                      ) : (
                        <>
                          <Play className="h-5 w-5 mr-2" />
                          Play Mindset Audio
                        </>
                      )}
                    </button>
                    <audio 
                      ref={mindsetAudioRef} 
                      onEnded={handleMindsetAudioEnded}
                      src={mindsetAudioData.audioUrl} 
                      type="audio/mp3"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                ) : (
                  <div className="alert">
                    <div>
                      <span className="text-sm md:text-base">No mindset audio available yet.</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Mental Imagery Section */}
          {(starterTasks.hasMentalImagery || mentalImageryAudioData) && (
            <div className="bg-white shadow-md rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-4 text-primary">Your Personalized Mental Imagery</h2>
              <p className="text-gray-600 mb-4">
                We've created a personalized mental imagery audio for you based on your profile. Listen to it to visualize peak performance and reinforce your mental training.
              </p>
              {/* Link to Mental Imagery - with audio playback */}
              <div className="flex justify-center">
                {audioLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="flex flex-col items-center">
                      <span className="loading loading-spinner loading-md text-primary mb-2"></span>
                      <span className="text-sm text-gray-600">Audio file is being generated...</span>
                    </div>
                  </div>
                ) : mentalImageryAudioData ? (
                  <div>
                    <button
                      onClick={mentalImageryAudioPlaying ? handleMentalImageryAudioPause : handleMentalImageryAudioPlay}
                      className="btn btn-accent"
                    >
                      {mentalImageryAudioPlaying ? (
                        <>
                          <Pause className="h-5 w-5 mr-2" />
                          Pause Mental Imagery Audio
                        </>
                      ) : (
                        <>
                          <Play className="h-5 w-5 mr-2" />
                          Play Mental Imagery Audio
                        </>
                      )}
                    </button>
                    <audio 
                      ref={mentalImageryAudioRef} 
                      onEnded={handleMentalImageryAudioEnded}
                      src={mentalImageryAudioData.audioUrl} 
                      type="audio/mp3"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                ) : (
                  <div className="alert">
                    <div>
                      <span className="text-sm md:text-base">No mental imagery audio available yet.</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Removed duplicate Mental Imagery section */}
          
          {/* Daily Check-In */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold mb-4 text-primary">Daily Check-In</h2>
            <DailyCheckIn />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
